import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { clipboardText, formatQuote, intentUrl, type ShareSource } from "../lib/shareToX";

interface ToolbarState {
  top: number;
  left: number;
  selection: string;
}

/**
 * Floating share toolbar over text selections — specs/share-to-x.md §2, §4.
 *
 * Wraps the reader; selections inside it (and only it) raise a small
 * [𝕏 Post] [Copy] toolbar above the selected range. Post opens X's own
 * compose window via the web intent — no API, no tokens; the user's x.com
 * session is the auth and they press Post themselves.
 */
export default function SelectionToolbar({
  source,
  children,
}: {
  source: ShareSource;
  children: ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<ToolbarState | null>(null);
  const [copied, setCopied] = useState(false);

  const dismiss = useCallback(() => {
    setState(null);
    setCopied(false);
  }, []);

  const evaluateSelection = useCallback(() => {
    const container = containerRef.current;
    const selection = window.getSelection();
    if (!container || !selection || selection.isCollapsed || selection.rangeCount === 0) {
      dismiss();
      return;
    }
    const range = selection.getRangeAt(0);
    const within =
      container.contains(range.startContainer) && container.contains(range.endContainer);
    const text = selection.toString().trim();
    if (!within || text.length < 3) {
      dismiss();
      return;
    }

    const rect = range.getBoundingClientRect();
    const host = container.getBoundingClientRect();
    const flipBelow = rect.top - host.top < 48;
    setState({
      top: flipBelow ? rect.bottom - host.top + 8 : rect.top - host.top - 44,
      left: Math.min(Math.max(rect.left - host.left + rect.width / 2, 70), host.width - 70),
      selection: text,
    });
    setCopied(false);
  }, [dismiss]);

  useEffect(() => {
    // mouseup/touchend catch the end of a drag; selectionchange catches
    // keyboard selection and collapse (which dismisses).
    const onEnd = () => setTimeout(evaluateSelection, 0);
    const onSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) dismiss();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismiss();
    };
    document.addEventListener("mouseup", onEnd);
    document.addEventListener("touchend", onEnd);
    document.addEventListener("selectionchange", onSelectionChange);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", dismiss, true);
    return () => {
      document.removeEventListener("mouseup", onEnd);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("selectionchange", onSelectionChange);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", dismiss, true);
    };
  }, [evaluateSelection, dismiss]);

  const post = state ? formatQuote(state.selection, source) : null;

  function handlePost() {
    if (!post) return;
    window.open(intentUrl(post), "_blank", "noopener,noreferrer");
    dismiss();
  }

  async function handleCopy() {
    if (!post) return;
    const text = clipboardText(post);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const area = document.createElement("textarea");
      area.value = text;
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      area.remove();
    }
    setCopied(true);
    setTimeout(dismiss, 900);
  }

  return (
    <div ref={containerRef} className="relative">
      {children}
      {state && post ? (
        <div
          ref={toolbarRef}
          role="toolbar"
          aria-label="Share selection"
          style={{ top: state.top, left: state.left }}
          className="absolute z-20 flex -translate-x-1/2 items-center overflow-hidden rounded-lg border border-border bg-surface-raised shadow-lg"
          // keep the selection alive while clicking the toolbar
          onMouseDown={(event) => event.preventDefault()}
        >
          <button
            type="button"
            onClick={handlePost}
            title={post.truncated ? "Quote shortened to fit the 280-character limit" : undefined}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-text hover:bg-accent-soft"
          >
            <span aria-hidden="true" className="font-semibold">
              𝕏
            </span>
            Post{post.truncated ? "*" : ""}
          </button>
          <span aria-hidden="true" className="h-5 w-px bg-border" />
          <button
            type="button"
            onClick={handleCopy}
            className="px-3 py-2 text-sm font-medium text-text hover:bg-accent-soft"
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
